/**
 * Desktop sidebar navigation with hover/focus route preload.
 *
 * Calls `preloadRouteIntent()` on `onMouseEnter` / `onFocus` for each nav item.
 * That warms translation JSON for the destination URL and, when access allows,
 * the matching lazy route JS chunk (desktop or mobile `routeId` from viewport snapshot).
 *
 * Preload is best-effort and never blocks click navigation.
 *
 * @see routes/routePreloaders.js
 * @see docs/runtime-preload.md
 */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout, Button, Divider } from "antd";
import { useTranslation } from "react-i18next";
import { IoIosArrowForward } from "react-icons/io";
import { LuPanelLeftClose, LuPanelLeftOpen } from "react-icons/lu";
import {
  getSidebarControlItems,
  getSidebarNavItems,
} from "modules/appModuleNavigation";
import { getDefaultAuthenticatedPath } from "modules/appModuleAccess";
import { isColorDark } from "utils/styleUtils";
import useAuthStore from "stores/useAuthStore";
import { useResolvedPostLoginBranding } from "hooks/useBranding";
import InteractiveDiv from "components/universal/InteractiveDiv/InteractiveDiv";
import { getRouteVariantSnapshot } from "hooks/useMobileViewport";
import { preloadRouteIntent } from "routes/routePreloaders";
import "./Sidebar.css";

const { Sider } = Layout;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const userData = useAuthStore((s) => s.userData);
  const { sidebarBranding, sidebarBackgroundColor } = useResolvedPostLoginBranding();
  const isSidebarBackgroundDark = isColorDark(sidebarBackgroundColor);

  const { t } = useTranslation("common");
  // Evaluator → /evaluations; misconfigured evaluator → /account/misconfigured; else /dashboard.
  const homePath = getDefaultAuthenticatedPath(userData);

  const [collapsed, setCollapsed] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => setCollapsed(window.innerWidth < 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const preloadRoute = (pathname) => {
    const { isMobileViewport } = getRouteVariantSnapshot();
    // Best-effort background warmup — never blocks click navigation.
    void preloadRouteIntent(pathname, userData, {
      variant: isMobileViewport ? "mobile" : "desktop",
    });
  };

  // Nav items filtered by userData.modules + role policy (see modules/appModuleNavigation).
  const moduleItems = getSidebarNavItems(userData, {
    t,
    pathname: location.pathname,
  });
  const controlItems = getSidebarControlItems(userData, {
    t,
    pathname: location.pathname,
  });

  return (
    <Sider
      width={220}
      theme="light"
      className="app-sidebar-container"
      padding={0}
      style={{ height: "inherit", background: sidebarBackgroundColor }}
      trigger={null}
      collapsible
      collapsed={collapsed}
      collapsedWidth={60}
    >
      <div
        className="app-sidebar-inner"
        data-theme={isSidebarBackgroundDark ? "dark" : "light"}
      >
        {/* ── Branding and nav controls ── */}
        <div className="app-sidebar-top">
          <div className="app-sidebar-brand">
            {(!collapsed && userData?.name) && (
              <p className="app-sidebar-brand-username">{userData.name}</p>
            )}

            {sidebarBranding?.type === "logo" && (
              <button
                type="button"
                className="app-sidebar-brand-logo-button"
                onClick={() => navigate(homePath)}
                aria-label="Go to home"
              >
                <img
                  src={sidebarBranding.value}
                  alt="Brand logo"
                  className="app-sidebar-brand-logo"
                />
              </button>
            )}
          </div>

          <div className="app-sidebar-controls">
            <Button
              icon={collapsed
                ? <LuPanelLeftOpen style={{ fontSize: 20 }} />
                : <LuPanelLeftClose style={{ fontSize: 20 }} />
              }
              onClick={() => setCollapsed(!collapsed)}
              className="app-sidebar-icon-button"
            />

            {controlItems.map((item) => {
              const Icon = item.Icon;
              return (
                <Button
                  key={item.key}
                  icon={<Icon />}
                  // Hover/focus: preload i18n JSON + authorized route chunk (see routePreloaders.js).
                  onMouseEnter={() => preloadRoute(item.to)}
                  onFocus={() => preloadRoute(item.to)}
                  onClick={() => navigate(item.to)}
                  className="app-sidebar-icon-button"
                  title={item.label}
                  aria-label={item.label}
                />
              );
            })}
          </div>

          <Divider className="app-sidebar-divider" />

          {/* ── App modules ── */}
          {moduleItems.length > 0 && (
            <nav className="app-sidebar-nav">
              <div className="app-sidebar-nav-clusters">
                {moduleItems.map((item) => {
                  const Icon = item.Icon;
                  return (
                    <InteractiveDiv
                      key={item.key}
                      className={`app-sidebar-nav-cluster-item ${item.className || ""} ${collapsed ? "app-sidebar-nav-cluster-item--collapsed" : ""}`}
                      // Hover/focus: preload i18n JSON + authorized route chunk (see routePreloaders.js).
                      onMouseEnter={() => preloadRoute(item.to)}
                      onFocus={() => preloadRoute(item.to)}
                      onClick={() => navigate(item.to)}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="app-sidebar-nav-cluster-icon">
                        <Icon />
                      </span>
                      {!collapsed && <span className="app-sidebar-nav-cluster-label">{item.label}</span>}
                      {!collapsed && item.isActive && <IoIosArrowForward className="app-sidebar-nav-cluster-arrow" />}
                    </InteractiveDiv>
                  );
                })}
              </div>
            </nav>
          )}
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;
